import { useState, useEffect } from 'react';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { ServiceOffering } from '../models/ServiceOffering';
import { logError } from '../../lib/utils';

export function useServices(organizationId?: string | null) {
  const [services, setServices] = useState<ServiceOffering[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // organizationId is currently unused but kept for API compatibility
  void organizationId;

  useEffect(() => {
    setLoading(true);
    
    // Services are global/shared - not tied to organization until assigned
    // Query all services, sorted by name
    // Query without orderBy to avoid index requirement - we'll sort in JavaScript
    const q = query(collection(db, 'services'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const servicesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          // Map the data structure from Firestore to the ServiceOffering model
          // Handle both old structure (price: {amount, period}) and new structure (defaultRate, unit)
          const mappedData: any = {
            id: doc.id,
            name: data.name || '',
            description: data.description || '',
            isActive: data.isActive ?? true,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            // Handle organizationId if it exists (for backward compatibility)
            organizationId: data.organizationId || '',
          };
          
          // Map price structure to defaultRate and unit
          if (data.price && typeof data.price === 'object' && data.price.amount !== undefined) {
            mappedData.defaultRate = data.price.amount || 0;
            // Map period to unit (monthly -> month, etc.)
            const periodMap: Record<string, string> = {
              'hourly': 'hour',
              'monthly': 'month',
              'quarterly': 'quarter',
              'semi-annual': 'semi-annual',
              'annual': 'year',
              'as-needed': 'project',
            };
            const period = data.price.period || 'monthly';
            mappedData.unit = periodMap[period] || period || 'month';
          } else if (data.defaultRate !== undefined) {
            // Use defaultRate and unit if they exist directly
            mappedData.defaultRate = data.defaultRate || 0;
            mappedData.unit = data.unit || 'month';
          } else {
            // Default values if neither structure exists
            mappedData.defaultRate = 0;
            mappedData.unit = 'month';
          }
          
          // Handle category if it exists
          if (data.category) {
            mappedData.category = data.category;
          }
          
          return mappedData as ServiceOffering;
        });
        
        // Sort by name (always sort in JavaScript to avoid index requirement)
        servicesData.sort((a, b) => a.name.localeCompare(b.name));
        
        setServices(servicesData);
        setLoading(false);
        setError(null);
      },
      (err: any) => {
        const errorMessage = err?.message || '';
        if (err?.code === 'failed-precondition' && (errorMessage.includes('index') || errorMessage.includes('Index'))) {
          setError(
            new Error(
              'Database indexes are currently building. Please wait a few minutes and refresh the page.',
            ),
          );
        } else {
          setError(err as Error);
        }
        logError('services', err, { source: 'useServices.subscription' });
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []); // No dependencies - services are global

  // Helper function to convert Date objects to Timestamps recursively
  const convertDatesToTimestamps = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    if (obj instanceof Date) return Timestamp.fromDate(obj);
    if (Array.isArray(obj)) return obj.map(convertDatesToTimestamps);
    if (typeof obj === 'object') {
      const converted: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          converted[key] = convertDatesToTimestamps(obj[key]);
        }
      }
      return converted;
    }
    return obj;
  };

  const createService = async (serviceData: Omit<ServiceOffering, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = Timestamp.now();
      // Convert any Date objects to Timestamps
      const convertedData = convertDatesToTimestamps(serviceData);
      await addDoc(collection(db, 'services'), {
        ...convertedData,
        createdAt: now,
        updatedAt: now,
      });
    } catch (err: unknown) {
      logError('services', err, { source: 'useServices.createService' });
      throw err;
    }
  };

  const updateService = async (serviceId: string, updates: Partial<ServiceOffering>) => {
    try {
      const serviceRef = doc(db, 'services', serviceId);
      await updateDoc(serviceRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (err: unknown) {
      logError('services', err, { source: 'useServices.updateService', serviceId });
      throw err;
    }
  };

  const deleteService = async (serviceId: string) => {
    try {
      const serviceRef = doc(db, 'services', serviceId);
      await deleteDoc(serviceRef);
    } catch (err: unknown) {
      logError('services', err, { source: 'useServices.deleteService', serviceId });
      throw err;
    }
  };

  return {
    services,
    loading,
    error,
    createService,
    updateService,
    deleteService,
  };
}
